import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/do-not-sleep-amnesia-ibiza-28-may-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/community/b0b39892-27f9-4476-af50-f4d20d64e09b.png";
const TICKET_URL = "https://www.ibiza-spotlight.com/night/promoters/do-not-sleep-amnesia";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const LINEUP = [
  "Richy Ahmed",
  "Gaskin",
  "M-High B2B Job de Jong",
  "Ellia Jaya",
  "Watchers",
];

const FAQS = [
  {
    question: "When is Do Not Sleep at Amnesia Ibiza?",
    answer: "Do Not Sleep at Amnesia Ibiza takes place on Thursday 28 May 2026, running from 23:00 to 06:00.",
  },
  {
    question: "Who is playing at Do Not Sleep Amnesia Ibiza?",
    answer: "The listed lineup includes Richy Ahmed, Gaskin, M-High B2B Job de Jong, Ellia Jaya and Watchers.",
  },
  {
    question: "Where is Do Not Sleep taking place?",
    answer: "The event takes place at Amnesia Ibiza, one of the island's most iconic house music clubs.",
  },
  {
    question: "What music should I expect?",
    answer: "Expect house, tech house, minimal, deep groove and late-night underground Ibiza energy.",
  },
  {
    question: "Can HLT help with hotels, tables and concierge for Amnesia Ibiza?",
    answer: "Yes. HLT can help with hotels, villas, transfers, VIP support, after-party planning and full concierge packages around Amnesia Ibiza events.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Do Not Sleep at Amnesia Ibiza 2026: Richy Ahmed, Gaskin and M-High B2B Job de Jong",
  description:
    "Do Not Sleep lands at Amnesia Ibiza on Thursday 28 May 2026 with Richy Ahmed, Gaskin, M-High B2B Job de Jong, Ellia Jaya and Watchers.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-05-28",
  dateModified: "2026-05-28",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "View Do Not Sleep Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Plan Amnesia with HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function DoNotSleepAmnesiaArticle() {
  return (
    <>
      <Helmet>
        <title>Do Not Sleep Amnesia Ibiza 2026 Tickets | HLT Ibiza Guide</title>
        <meta
          name="description"
          content="Do Not Sleep at Amnesia Ibiza with Richy Ahmed, Gaskin, M-High B2B Job de Jong, Ellia Jaya and Watchers. Plan tickets, hotels and concierge with HLT."
        />
        <meta
          name="keywords"
          content="Do Not Sleep Ibiza 2026, Do Not Sleep Amnesia tickets, Richy Ahmed Ibiza, Gaskin Ibiza, M-High Job de Jong Ibiza, Amnesia Ibiza May 2026, Ibiza house music events, Ibiza tech house events, HLT Ibiza concierge, Ibiza hotel and ticket package"
        />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Do Not Sleep Amnesia Ibiza 2026 Tickets | HLT Ibiza Guide" />
        <meta property="og:description" content="Plan Do Not Sleep at Amnesia Ibiza with HLT tickets, hotels, VIP support and concierge planning." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Do Not Sleep Amnesia Ibiza 2026 Tickets" />
        <meta name="twitter:description" content="Do Not Sleep lands at Amnesia Ibiza with Richy Ahmed, Gaskin, M-High B2B Job de Jong, Ellia Jaya and Watchers." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">Ibiza 2026 guide</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Do Not Sleep at Amnesia Ibiza 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Richy Ahmed, Gaskin, M-High B2B Job de Jong, Ellia Jaya and Watchers take Amnesia into a late-night house and tech house session on Thursday 28 May 2026.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta />
              <ConciergeCta children="Build the Ibiza night with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>Do Not Sleep</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Amnesia Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Thursday 28 May 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:00 - 06:00</dd></div>
              <div><dt className="text-white">Starting price</dt><dd>From GBP 43.31</dd></div>
              <div><dt className="text-white">Sound</dt><dd>House, tech house, minimal and groove-led Ibiza club music</dd></div>
            </dl>
          </aside>
        </header>

        <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden border border-brand-500/25 bg-black shadow-[0_24px_90px_rgba(0,0,0,0.55)] transition hover:border-brand-500/70">
          <img src="/assets/community/b0b39892-27f9-4476-af50-f4d20d64e09b.png" alt="Do Not Sleep at Amnesia Ibiza event poster" className="block h-auto w-full transition duration-500 group-hover:scale-[1.01]" />
        </a>

        <div className="mx-auto max-w-4xl space-y-10">
          <section className="space-y-4 text-base leading-8 text-slate-300">
            <p>
              Do Not Sleep at Amnesia Ibiza is exactly the type of night HLT wants to put in front of house music travellers: a serious venue, a clear sound and a lineup built for people who care about the dancefloor.
            </p>
            <p>
              The Thursday 28 May 2026 edition brings Richy Ahmed, Gaskin, M-High B2B Job de Jong, Ellia Jaya and Watchers into Amnesia for a 23:00 to 06:00 session. It is a late-night Ibiza event with the right balance of recognised names, underground movement and proper club energy.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Do Not Sleep ticket options" />
              <ConciergeCta children="Ask HLT to build the full Ibiza plan" />
            </div>
          </section>

          <ArticleSection title="Why Do Not Sleep still matters in Ibiza">
            <p>
              Do Not Sleep has always made sense in Ibiza because the brand is not built around the most obvious, commercial version of the island. It is built around rhythm, tension, late-night energy and a crowd that wants to stay locked into the music.
            </p>
            <p>
              That is why the concept works at Amnesia. The club has the scale and history to make a night feel major, but it still has the raw room energy needed for house and tech house when the programming is right.
            </p>
            <p>
              For HLT, this is an ideal event to spotlight because it gives travellers a simple reason to build the night properly: secure the ticket, choose the right area to stay, sort transport and make the whole Ibiza plan feel intentional.
            </p>
          </ArticleSection>

          <ArticleSection title="The lineup: Richy Ahmed, Gaskin, M-High B2B Job de Jong and more">
            <p>
              Richy Ahmed brings the kind of house music identity that has long connected the UK scene with Ibiza. His sets often move between groove, warmth and club pressure, which makes him a natural fit for a late-night Amnesia session.
            </p>
            <p>
              Gaskin adds a sharper modern tech house edge. His rise has come from dancefloor-focused records, heavy club support and a sound that works for packed rooms without losing its underground feel.
            </p>
            <p>
              M-High B2B Job de Jong gives the night a deeper, more rolling lane. That back-to-back should appeal to people who like minimal detail, momentum and selectors who know how to hold a room rather than rush it.
            </p>
            <p>
              Ellia Jaya and Watchers complete a lineup that feels built for movement from start to finish. This is not a one-name poster. It is a full night shaped around house music that can carry through until morning.
            </p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              {LINEUP.map((artist) => <li key={artist}>{artist}</li>)}
            </ul>
          </ArticleSection>

          <ArticleSection title="Why Amnesia is the right room for this night">
            <p>
              Amnesia is one of Ibiza's landmark clubs, and nights like this need that kind of room. The venue gives Do Not Sleep the scale to feel like a proper Ibiza moment while keeping the focus on the music and the crowd.
            </p>
            <p>
              If you are planning a house music trip, Amnesia is the kind of venue that can anchor a whole night. You can build dinner, pre-drinks, transport, tickets and after-hours plans around it without the trip feeling scattered.
            </p>
          </ArticleSection>

          <ArticleSection title="Build the trip around the night">
            <p>
              Some people only need the ticket. Others want the full Ibiza setup: hotel, villa, transfers, restaurants, VIP options, beach clubs, after parties and a plan that makes the night easier from start to finish.
            </p>
            <p>Through HLT, you can build around Do Not Sleep with:</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Do Not Sleep ticket access</li>
              <li>Hotels near Ibiza Town, San Rafael, Talamanca or Playa d'en Bossa</li>
              <li>Group villas for private pre-parties and recovery days</li>
              <li>Airport transfers and club transport</li>
              <li>VIP table enquiries and premium nightlife support</li>
              <li>After-party, villa party and private group planning</li>
              <li>Full Ibiza packages combining tickets, hotels, transfers and concierge help</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View ticket options" />
              <ConciergeCta children="Request an Ibiza concierge package" />
              <Link to={EVENTS_URL} className="btn-secondary">Browse more Ibiza events</Link>
            </div>
          </ArticleSection>

          <ArticleSection title="Where to stay for Amnesia Ibiza">
            <p>
              Amnesia sits near San Rafael, so where you stay depends on the wider trip. Ibiza Town and Talamanca are strong for restaurants, marina energy and a more polished hotel base. Playa d'en Bossa works if you want to combine Amnesia with Hi Ibiza, Ushuaia and beach clubs.
            </p>
            <p>
              San Antonio can work for more budget-conscious groups, but transport needs to be planned properly. Villas are ideal for groups who want privacy, pool days and a better base for pre-party or after-party plans.
            </p>
            <ConciergeCta children="Ask HLT where to stay for Amnesia" />
          </ArticleSection>

          <ArticleSection title="Who should go?">
            <p>
              This is the right event if your group is into Richy Ahmed, Gaskin, minimal house, tech house, underground house, late-night Ibiza parties and venues that still feel like real club rooms.
            </p>
            <p>
              It is also a smart pick for people who want one strong Amnesia night as the centrepiece of a short Ibiza trip. The lineup gives the night enough weight to justify building around it.
            </p>
          </ArticleSection>

          <ArticleSection title="Final word">
            <p>
              Do Not Sleep at Amnesia Ibiza is a proper HLT-style event: music-led, late-night, venue-heavy and ideal for travellers who want more than a random night out.
            </p>
            <p>
              If you are going to Ibiza around Thursday 28 May 2026, this is one of the nights to look at first. Book the ticket, plan the hotel and let the music shape the trip.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Do Not Sleep tickets" />
              <ConciergeCta children="Build the trip with HLT" />
            </div>
          </ArticleSection>

          <section className="border-t border-white/10 pt-8">
            <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
            <div className="mt-5 grid gap-3">
              {FAQS.map((item) => (
                <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                  <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
